"use client";

import { useEffect, useId, useRef, useState } from "react";

import { RecordOptionLabel } from "@product/app/workspace/channels/[channelId]/record-option-badge";
import { RecordResourceLabel } from "@product/app/workspace/channels/[channelId]/record-resource-label";
import {
  RecordFieldFrame,
  RecordFieldGrid,
  RecordSheetDetailsBody,
  RecordSheetFooter,
  RecordSheetNavigation,
  RecordSheetTabRow,
  RecordSystemValue,
} from "@product/app/workspace/channels/[channelId]/record-sheet-view";
import { WorkspaceNotificationWatchButtonView } from "@product/components/app/workspace/workspace-notification-watch-button-view";
import { RecordImageViewer } from "@product/components/content/record-image-viewer";
import {
  SheetCloseContent,
  SheetHeaderView,
  sheetDescriptionClassName,
  sheetHeaderCloseClassName,
  sheetTitleClassName,
  sheetVariants,
} from "@product/components/ui/sheet";
import { cn } from "@product/lib/utils";

import type { ShotSheetRecord } from "./sheet-record";

const noop = () => {};

/**
 * A record's sheet as RecordEditorSheet draws it for a record whose fields a
 * workflow owns: header, Details and Discussion, the photographs with
 * Compare images, the field grid and the footer. The parts are the
 * application's; only the frame differs. The app mounts the sheet in a
 * viewport-fixed portal, the shot places the same classes absolutely inside
 * its window, with the same scrim. Discussion stays on Details: it would
 * need free text. Opened from an answer that links several records, the
 * header steps through them, as RecordLinkSheetProvider's sheet does.
 */
export function ShotRecordSheet({
  record,
  onClose,
  focusOnOpen = true,
  navigation = null,
  onNavigate,
}: {
  record: ShotSheetRecord;
  onClose: () => void;
  /** Move focus into the sheet, as a visitor's own click does. A replay
   *  opening it by itself leaves focus where the visitor put it. */
  focusOnOpen?: boolean;
  /** Where the record sits among the records its answer links. */
  navigation?: { index: number; total: number } | null;
  onNavigate?: (direction: -1 | 1) => void;
}) {
  const [watched, setWatched] = useState(false);
  const closeRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();

  useEffect(() => {
    if (focusOnOpen) closeRef.current?.focus();
  }, [focusOnOpen, record.id]);

  return (
    <>
      {/* SheetOverlay's scrim; outside presses close the sheet. */}
      <div aria-hidden="true" className="absolute inset-0 z-(--z-backdrop) bg-black/10 dark:bg-black/40" onClick={onClose} />
      <div
        role="dialog"
        aria-labelledby={titleId}
        data-slot="sheet-content"
        data-side="right"
        data-size="lg"
        data-open=""
        onKeyDown={(event) => {
          if (event.key === "Escape") onClose();
        }}
        className={cn(
          sheetVariants({ side: "right", size: "lg" }),
          "flex flex-col overflow-hidden",
          "absolute motion-reduce:animate-none!",
        )}
      >
        <SheetHeaderView
          className="border-b-0"
          actions={
            navigation ? <RecordSheetNavigation navigation={navigation} onNavigate={onNavigate} /> : undefined
          }
          close={
            <button
              ref={closeRef}
              type="button"
              data-slot="sheet-header-close"
              className={sheetHeaderCloseClassName}
              onClick={onClose}
            >
              <SheetCloseContent />
            </button>
          }
        >
          <h2 id={titleId} data-slot="sheet-title" className={sheetTitleClassName}>
            {record.title}
          </h2>
          <p data-slot="sheet-description" className={sheetDescriptionClassName}>
            {record.dataSetName}
          </p>
        </SheetHeaderView>
        <RecordSheetTabRow
          tab="details"
          discussionCount={0}
          actions={
            <WorkspaceNotificationWatchButtonView
              watched={watched}
              onToggle={() => setWatched((current) => !current)}
            />
          }
        />
        <RecordSheetDetailsBody>
          <RecordImageViewer images={record.photographs} />
          <RecordFieldGrid>
            {record.fields.map((field) => (
              <RecordFieldFrame
                key={field.id}
                label={field.label}
                required={field.required}
                compact={field.compact}
              >
                <RecordSystemValue>
                  {field.cell.kind === "file" ? (
                    <RecordResourceLabel type="file" title={field.cell.title} href={field.cell.href} />
                  ) : field.cell.kind === "option" ? (
                    <RecordOptionLabel option={field.cell.option} />
                  ) : (
                    field.cell.display
                  )}
                </RecordSystemValue>
              </RecordFieldFrame>
            ))}
          </RecordFieldGrid>
        </RecordSheetDetailsBody>
        <RecordSheetFooter
          showArchive
          saving={false}
          saveDisabled
          onArchive={noop}
          onCancel={onClose}
          onSave={noop}
        />
      </div>
    </>
  );
}
